import React, { useState } from 'react'
import { ImageIcon, Plus, Trash2, X, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { NewProduct, ImageFile } from '@/app/services/inventory.service'
import { FormError } from '../../ui/FormError'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import {
  calculateFinalPrice,
  calculateSalePrice,
  recalculateCosts,
  calculateTierPrices,
} from '../../../services/productCalculations.service'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const EMPTY_PRODUCT: NewProduct = {
  name: '',
  description: '',
  barcode: '',
  heroImage: null,
  galleryImages: [],
  categoryId: '',
  departmentId: '',
  productTypeId: '',
  unitId: '',
  satProductKeyId: '',
  costWithoutTaxes: '',
  costWithTaxes: '',
  profitPercentage: '30',
  taxId: '',
  batchNumber: '',
  expirationDate: '',
  initialStock: '',
  minStock: '',
  maxStock: '',
  pricingTiers: [],
}

// ─── Esquemas de validación ─────────────────────────────────────────────────
const productSchema = z.object({
  name: z.string().min(4, 'El nombre es requerido'),
  barcode: z.string().min(1, 'El código de barras es requerido'),
  description: z.string().optional(),
  heroImage: z.any().optional(),
  galleryImages: z.array(z.any()).optional(),
  categoryId: z.string().min(1, 'La categoría es requerida'),
  departmentId: z.string().min(1, 'El departamento es requerido'),
  productTypeId: z.string().min(1, 'El tipo de producto es requerido'),
  unitId: z.string().min(1, 'La unidad es requerida'),
  satProductKeyId: z.string().optional(),
  costWithoutTaxes: z.string().min(1, 'El costo es requerido'),
  costWithTaxes: z.string().min(1, 'El costo con impuestos es requerido'),
  profitPercentage: z.string().min(1, 'El porcentaje de ganancia es requerido'),
  taxId: z.string().optional(),
  batchNumber: z.string().optional(),
  expirationDate: z.string().optional(),
  initialStock: z.string().min(1, 'El stock inicial es requerido'),
  minStock: z.string().min(1, 'El stock mínimo es requerido'),
  maxStock: z.string().min(1, 'El stock máximo es requerido'),
  pricingTiers: z.array(z.any()).optional(),
}).superRefine((data, ctx) => {
  if (data.productTypeId === '2' || data.productTypeId === '3') {
    if (!data.batchNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El número de lote es requerido',
        path: ['batchNumber']
      });
    }
    if (!data.expirationDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La fecha de expiración es requerida',
        path: ['expirationDate']
      });
    }
  }
});

type ProductFormValidate = z.infer<typeof productSchema>

/**
 * Crea un objeto ImageFile a partir de un archivo
 */
function createImageFile(file: File, sortOrder?: number): ImageFile {
  return { file, previewUrl: URL.createObjectURL(file), sortOrder }
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
interface AddProductModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAddProduct: (product: NewProduct) => void
}

export function AddProductModal({
  open,
  onOpenChange,
  onAddProduct,
}: AddProductModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormValidate>({
    resolver: zodResolver(productSchema),
    mode: 'onChange',
    defaultValues: EMPTY_PRODUCT,
    shouldFocusError: true
  })

  const { fields: pricingTiers, append: appendTier, remove: removeTier, update: updateTier } = useFieldArray({
    control,
    name: 'pricingTiers',
  })

  // Watchers for calculated fields
  const costWithoutTaxes = watch('costWithoutTaxes')
  const costWithTaxes = watch('costWithTaxes')
  const profitPercentage = watch('profitPercentage')
  const taxId = watch('taxId')
  const productTypeId = watch('productTypeId')
  const heroImage = watch('heroImage')
  const galleryImages = watch('galleryImages') || []
  const tiersValues = watch('pricingTiers') || []

  // ---------------------------------------------------------------------------
  // Manejadores de imágenes
  // ---------------------------------------------------------------------------
  const handleHeroImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    setValue('heroImage', createImageFile(file), { shouldValidate: true })
    e.target.value = ''
  }

  const handleRemoveHeroImage = () => {
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    setValue('heroImage', null, { shouldValidate: true })
  }

  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const incoming = Array.from(e.target.files).map((file, i) =>
      createImageFile(file, galleryImages.length + i),
    )
    setValue('galleryImages', [...galleryImages, ...incoming], { shouldValidate: true })
    e.target.value = ''
  }

  const handleRemoveGalleryImage = (index: number) => {
    URL.revokeObjectURL(galleryImages[index].previewUrl)
    const updated = galleryImages
      .filter((_, i) => i !== index)
      .map((img, i) => ({ ...img, sortOrder: i }))
    setValue('galleryImages', updated, { shouldValidate: true })
  }

  // ---------------------------------------------------------------------------
  // Envio y Reset
  // ---------------------------------------------------------------------------
  const onSubmit = (data: ProductFormValidate) => {
    onAddProduct(data as NewProduct)
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    galleryImages.forEach((img: ImageFile) => URL.revokeObjectURL(img.previewUrl))
    reset(EMPTY_PRODUCT)
  }

  // ---------------------------------------------------------------------------
  // Render del componente
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Producto</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario completo para registrar un nuevo producto en el inventario
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <fieldset className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <legend className="sr-only">Información básica</legend>
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nombre del Producto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Input
                id='name'
                {...register('name')}
                placeholder="Ej: Paracetamol 500mg"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              <FormError id="name-error" message={errors.name?.message} />
            </div>
            <div className="space-y-2">
              <label htmlFor="barcode" className="text-sm font-medium text-slate-700">
                Código de Barras <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Input
                id='barcode'
                {...register('barcode')}
                placeholder="7501234567890"
                aria-invalid={!!errors.barcode}
                aria-describedby={errors.barcode ? "barcode-error" : undefined}
              />
              <FormError id="barcode-error" message={errors.barcode?.message} />
            </div>
          </fieldset>

          {/* Categoría, Departamento y Tipo */}
          <fieldset className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <legend className="sr-only">Clasificación del Producto</legend>
            <div className="space-y-2">
              <label htmlFor="categoryId" className="text-sm font-medium text-slate-700">
                Categoría <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="categoryId"
                      aria-invalid={!!errors.categoryId}
                      aria-describedby={errors.categoryId ? "categoryId-error" : undefined}
                    >
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Medicamentos</SelectItem>
                      <SelectItem value="2">Vitaminas y Suplementos</SelectItem>
                      <SelectItem value="3">Dermocosmética</SelectItem>
                      <SelectItem value="4">Higiene Personal</SelectItem>
                      <SelectItem value="5">Bebés y Maternidad</SelectItem>
                      <SelectItem value="6">Nutrición</SelectItem>
                      <SelectItem value="7">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError id="categoryId-error" message={errors.categoryId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="departmentId" className="text-sm font-medium text-slate-700">
                Departamento <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="departmentId"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="departmentId"
                      aria-invalid={!!errors.departmentId}
                      aria-describedby={errors.departmentId ? "departmentId-error" : undefined}
                    >
                      <SelectValue placeholder="Seleccionar departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Antibióticos</SelectItem>
                      <SelectItem value="2">Antigripales</SelectItem>
                      <SelectItem value="3">Analgésicos</SelectItem>
                      <SelectItem value="4">Cardiovascular</SelectItem>
                      <SelectItem value="5">Gastrointestinal</SelectItem>
                      <SelectItem value="6">Dermatología</SelectItem>
                      <SelectItem value="7">Vitaminas y Suplementos</SelectItem>
                      <SelectItem value="8">Cuidado Personal</SelectItem>
                      <SelectItem value="9">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError id="departmentId-error" message={errors.departmentId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="productTypeId" className="text-sm font-medium text-slate-700">
                Tipo de Producto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="productTypeId"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="productTypeId"
                      aria-invalid={!!errors.productTypeId}
                      aria-describedby={errors.productTypeId ? "productTypeId-error" : undefined}
                    >
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Venta Libre</SelectItem>
                      <SelectItem value="2">Antibiótico</SelectItem>
                      <SelectItem value="3">Medicamento Controlado</SelectItem>
                      <SelectItem value="4">Grupo IV</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError id="productTypeId-error" message={errors.productTypeId?.message} />
            </div>
          </fieldset>

          {/* Lote y caducidad — para antibióticos y controlados */}
          <div aria-live="polite" role="status">
            {(productTypeId === '2' || productTypeId === '3') && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="batchNumber" className="text-sm font-medium text-yellow-900">
                      Número de Lote <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                    </label>
                    <Input
                      id='batchNumber'
                      {...register('batchNumber')}
                      placeholder="LOT-2024-XXXX"
                      aria-invalid={!!errors.batchNumber}
                      aria-describedby={errors.batchNumber ? "batchNumber-error" : undefined}
                    />
                    <p className="text-xs text-yellow-700">
                      Requerido para{' '}
                      {productTypeId === '2' ? 'antibióticos' : 'medicamentos controlados'}
                    </p>
                    <FormError id="batchNumber-error" message={errors.batchNumber?.message} />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="expirationDate" className="text-sm font-medium text-yellow-900">
                      Fecha de Caducidad <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                    </label>
                    <Input
                      id='expirationDate'
                      type="date"
                      {...register('expirationDate')}
                      aria-invalid={!!errors.expirationDate}
                      aria-describedby={errors.expirationDate ? "expirationDate-error" : undefined}
                    />
                    <FormError id="expirationDate-error" message={errors.expirationDate?.message} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Grupo IV */}
          {productTypeId === '4' && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-1 text-sm font-medium text-blue-900">Producto Grupo IV</p>
              <p className="text-xs text-blue-700">
                Este tipo requiere solicitud de receta médica, pero no requiere número de
                lote ni fecha de caducidad.
              </p>
            </div>
          )}

          {/* Unidad y clave SAT */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="unitId" className="text-sm font-medium text-slate-700">
                Unidad de Medida <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>
              <Controller
                name="unitId"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="unitId"
                      aria-invalid={!!errors.unitId}
                      aria-describedby={errors.unitId ? "unitId-error" : undefined}
                    >
                      <SelectValue placeholder="Seleccionar unidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Piezas</SelectItem>
                      <SelectItem value="2">Cajas</SelectItem>
                      <SelectItem value="3">Frascos</SelectItem>
                      <SelectItem value="4">Sobres</SelectItem>
                      <SelectItem value="5">Tubos</SelectItem>
                      <SelectItem value="6">Ampolletas</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError id="unitId-error" message={errors.unitId?.message} />
            </div>

            <div className="space-y-2">
              <label htmlFor="satProductKeyId" className="text-sm font-medium text-slate-700">Clave SAT</label>
              <Controller
                name="satProductKeyId"
                control={control}
                render={({ field }) => (
                  <Select id='satProductKeyId' value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Buscar clave SAT" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">51121701 - Analgésicos</SelectItem>
                      <SelectItem value="2">51121702 - Antiinflamatorios</SelectItem>
                      <SelectItem value="3">51101506 - Antibióticos (penicilinas)</SelectItem>
                      <SelectItem value="4">51101507 - Antibióticos (cefalosporinas)</SelectItem>
                      <SelectItem value="5">51101801 - Antihistamínicos</SelectItem>
                      <SelectItem value="6">51171701 - Antiulcerosos</SelectItem>
                      <SelectItem value="7">51171501 - Antidiarreicos</SelectItem>
                      <SelectItem value="8">51141602 - Antihipertensivos</SelectItem>
                      <SelectItem value="9">51181602 - Antidiabéticos orales</SelectItem>
                      <SelectItem value="10">51151603 - Vitaminas y minerales</SelectItem>
                      <SelectItem value="11">51151501 - Suplementos alimenticios</SelectItem>
                      <SelectItem value="12">53131626 - Cremas y pomadas dermatológicas</SelectItem>
                      <SelectItem value="13">42151501 - Productos para la piel</SelectItem>
                      <SelectItem value="14">42151502 - Productos para el cabello</SelectItem>
                      <SelectItem value="15">53121601 - Jeringas y agujas</SelectItem>
                      <SelectItem value="16">42294801 - Pañales</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError message={errors.satProductKeyId?.message} />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor='description' className="text-sm font-medium text-slate-700">Descripción</label>
            <Textarea
              id='description'
              {...register('description')}
              placeholder="Descripción detallada del producto..."
              rows={3}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? "description-error" : undefined}
            />
            <FormError id="description-error" message={errors.description?.message} />
          </div>

          {/* Imágenes */}
          <fieldset className="space-y-4">
            <legend className="sr-only">Imágenes del Producto</legend>
            <h4 className="text-sm font-medium text-slate-900" aria-hidden="true">Imágenes del Producto</h4>

            {/* Imagen principal */}
            <div className="flex flex-col space-y-2 items-start">
              <label htmlFor='heroImage' className="text-sm font-medium text-slate-700">
                Imagen Principal <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
              </label>

              {heroImage ? (
                <div className="group relative inline-block">
                  <img
                    src={heroImage.previewUrl}
                    alt="Imagen principal"
                    className="h-40 w-40 rounded-lg border border-slate-200 object-cover shadow-sm"
                  />
                  <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <label
                      htmlFor="hero-image-replace"
                      className="cursor-pointer rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                      title="Cambiar imagen"
                    >
                      <Upload className="h-4 w-4 text-slate-700" aria-hidden="true" />
                      <span className="sr-only">Reemplazar imagen principal</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveHeroImage}
                      className="rounded-full bg-red-500/90 p-1.5 shadow transition hover:bg-red-600"
                      title="Eliminar imagen"
                      aria-label="Eliminar imagen principal"
                    >
                      <X className="h-4 w-4 text-white" aria-hidden="true" />
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageUpload}
                    className="hidden"
                    id="hero-image-replace"
                  />
                  <span className="mt-1 block max-w-[160px] truncate text-xs text-slate-500">
                    {heroImage.file.name}
                  </span>
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition hover:border-slate-300">
                  <input
                    id="hero-image"
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageUpload}
                    className="hidden"
                  />
                  <label htmlFor="hero-image" className="cursor-pointer">
                    <ImageIcon className="mx-auto mb-2 h-10 w-10 text-slate-400" />
                    <p className="text-sm font-medium text-slate-600">
                      Haz clic para subir la imagen principal
                    </p>
                    <p className="mt-1 text-xs text-slate-500">PNG, JPG hasta 5 MB</p>
                  </label>
                </div>
              )}
            </div>

            {/* Galería */}
            <div className="space-y-2">
              <label htmlFor='galleryImages' className="text-sm font-medium text-slate-700">
                Galería de Imágenes
                <span className="ml-1 text-xs font-normal text-slate-500">
                  (opcional — múltiples)
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                {galleryImages.map((img: ImageFile, index: number) => (
                  <div key={index} className="group relative">
                    <img
                      src={img.previewUrl}
                      alt={`Galería ${index + 1}`}
                      className="h-24 w-24 rounded-lg border border-slate-200 object-cover shadow-sm"
                    />
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleRemoveGalleryImage(index)}
                        className="rounded-full bg-red-500/90 p-1 shadow transition hover:bg-red-600"
                        title="Eliminar"
                        aria-label={`Eliminar imagen de galería ${index + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                      </button>
                    </div>
                    <span className="absolute bottom-1 right-1 rounded bg-black/50 px-1 text-[10px] text-white">
                      {index + 1}
                    </span>
                  </div>
                ))}

                <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 transition hover:border-slate-300">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                    id="gallery-images"
                  />
                  <label
                    htmlFor="gallery-images"
                    className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1"
                  >
                    <Plus className="h-6 w-6 text-slate-400" />
                    <span className="text-[11px] text-slate-500">Agregar</span>
                  </label>
                </div>
              </div>

              {galleryImages.length > 0 && (
                <p className="text-xs text-slate-500">
                  {galleryImages.length} imagen
                  {galleryImages.length !== 1 ? 'es' : ''} en galería. El orden se refleja en la numeración.
                </p>
              )}
            </div>
          </fieldset>

          {/* Impuesto */}
          <fieldset className="space-y-3 rounded-lg bg-slate-50 p-4">
            <legend className="sr-only">Impuestos</legend>
            <h4 className="text-sm font-medium text-slate-900" aria-hidden="true">
              Impuesto para Precio Final al Público
            </h4>
            <p className="text-xs text-slate-600">
              Selecciona el impuesto que aplica a este producto (solo uno)
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="apply-iva"
                  checked={taxId === 'iva'}
                  onCheckedChange={(checked) => {
                    const val = checked ? 'iva' : '';
                    const calc = recalculateCosts(costWithoutTaxes, taxId, 'tax', val)
                    setValue('taxId', val, { shouldValidate: true })
                    if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes)
                  }}
                />
                <label htmlFor="apply-iva" className="cursor-pointer text-sm text-slate-700">
                  Agregar IVA (16%) al precio final
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="apply-ieps"
                  checked={taxId === 'ieps'}
                  onCheckedChange={(checked) => {
                    const val = checked ? 'ieps' : '';
                    const calc = recalculateCosts(costWithoutTaxes, taxId, 'tax', val)
                    setValue('taxId', val, { shouldValidate: true })
                    if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes)
                  }}
                />
                <label htmlFor="apply-ieps" className="cursor-pointer text-sm text-slate-700">
                  Agregar IEPS (8%) al precio final
                </label>
              </div>
            </div>
            <FormError message={errors.taxId?.message} />
          </fieldset>

          {/* Costos */}
          <fieldset className="space-y-3 rounded-lg bg-blue-50 p-4">
            <legend className="sr-only">Costos del Producto</legend>
            <h4 className="text-sm font-medium text-blue-900" aria-hidden="true">Costo del Producto</h4>
            <p className="text-xs text-blue-700">
              Ingresa el costo con o sin impuesto; el otro se calculará automáticamente
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor='costWithoutTaxes' className="text-sm font-medium text-blue-900">
                  Costo sin Impuesto <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                </label>
                <Input
                  id='costWithoutTaxes'
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('costWithoutTaxes', {
                    onChange: (e) => {
                      const calc = recalculateCosts(costWithoutTaxes, taxId, 'without', e.target.value)
                      if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes, { shouldValidate: true })
                    }
                  })}
                  placeholder="0.00"
                  aria-invalid={!!errors.costWithoutTaxes}
                  aria-describedby={errors.costWithoutTaxes ? "costWithoutTaxes-error" : undefined}
                />
                <FormError id="costWithoutTaxes-error" message={errors.costWithoutTaxes?.message} />
              </div>
              <div className="space-y-2">
                <label htmlFor='costWithTaxes' className="text-sm font-medium text-blue-900">
                  Costo con Impuesto
                </label>
                <Input
                  id='costWithTaxes'
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('costWithTaxes', {
                    onChange: (e) => {
                      const calc = recalculateCosts(costWithoutTaxes, taxId, 'with', e.target.value)
                      if (calc.costWithoutTaxes !== undefined) setValue('costWithoutTaxes', calc.costWithoutTaxes, { shouldValidate: true })
                    }
                  })}
                  placeholder="0.00"
                  aria-invalid={!!errors.costWithTaxes}
                  aria-describedby={errors.costWithTaxes ? "costWithTaxes-error" : undefined}
                />
                <FormError id="costWithTaxes-error" message={errors.costWithTaxes?.message} />
              </div>
            </div>
            {!taxId && (
              <p className="rounded bg-orange-100 p-2 text-xs text-orange-700">
                Selecciona un impuesto arriba para habilitar el cálculo automático entre costos
              </p>
            )}
          </fieldset>

          {/* Margen de utilidad y precio */}
          <div className="space-y-2">
            <label htmlFor='profitPercentage' className="text-sm font-medium text-slate-700">
              Porcentaje de Utilidad <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
            </label>
            <div className="flex items-center gap-3">
              <Input
                id='profitPercentage'
                type="number"
                min={0}
                max={500}
                step={1}
                {...register('profitPercentage')}
                placeholder="30"
                className="flex-1"
                aria-invalid={!!errors.profitPercentage}
                aria-describedby={errors.profitPercentage ? "profitPercentage-error" : undefined}
              />
              <span className="text-2xl font-semibold text-blue-700">
                {profitPercentage || 0}%
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Porcentaje de ganancia sobre el costo sin impuesto
            </p>
            <FormError id="profitPercentage-error" message={errors.profitPercentage?.message} />
          </div>

          {/* Resumen de precio */}
          {costWithoutTaxes && profitPercentage && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-live="polite" role="status">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="mb-1 text-xs text-slate-600">Precio de venta base (sin imp.)</p>
                <p className="text-xl font-semibold text-slate-800">
                  ${calculateSalePrice(costWithoutTaxes, profitPercentage)}
                </p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="mb-1 text-xs text-green-700">Precio final al público</p>
                <p className="text-2xl font-semibold text-green-700">
                  ${calculateFinalPrice(costWithoutTaxes, profitPercentage, taxId)}
                </p>
                <div className="mt-1 space-y-0.5 text-xs text-green-600">
                  <p>Base: ${calculateSalePrice(costWithoutTaxes, profitPercentage)}</p>
                  {taxId === 'iva' && (
                    <p>
                      + IVA (16%): $
                      {(parseFloat(calculateSalePrice(costWithoutTaxes, profitPercentage)) * 0.16).toFixed(2)}
                    </p>
                  )}
                  {taxId === 'ieps' && (
                    <p>
                      + IEPS (8%): $
                      {(parseFloat(calculateSalePrice(costWithoutTaxes, profitPercentage)) * 0.08).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Precios por cantidad */}
          <div className="space-y-3 rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-purple-900">Precios por Cantidad</h5>
                <p className="mt-0.5 text-xs text-purple-700">
                  Configura descuentos o utilidades distintas según la cantidad mínima vendida
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() =>
                  appendTier({
                    minQuantity: 1,
                    profitPercentage: 0,
                    discountPercentage: 0,
                    price: '',
                    priceWithTax: '',
                  })
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Agregar Nivel
              </Button>
            </div>

            {pricingTiers.length > 0 ? (
              <div className="space-y-2">
                {pricingTiers.map((tier, index) => {
                  const currentTierVal = tiersValues[index] || tier
                  const tierCalc = calculateTierPrices(
                    costWithoutTaxes,
                    parseFloat(currentTierVal.profitPercentage) || 0,
                    parseFloat(currentTierVal.discountPercentage) || 0,
                    taxId
                  )

                  return (
                    <div
                      key={tier.id}
                      className="grid grid-cols-5 items-end gap-2 rounded-lg bg-white p-3"
                    >
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.minQuantity`} className="text-xs text-slate-600">Cant. mínima</label>
                        <Input
                          id={`pricingTiers.${index}.minQuantity`}
                          type="number"
                          min={1}
                          {...register(`pricingTiers.${index}.minQuantity` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.profitPercentage`} className="text-xs text-slate-600">Utilidad %</label>
                        <Input
                          id={`pricingTiers.${index}.profitPercentage`}
                          type="number"
                          min={0}
                          max={500}
                          {...register(`pricingTiers.${index}.profitPercentage` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.discountPercentage`} className="text-xs text-slate-600">Descuento %</label>
                        <Input
                          id={`pricingTiers.${index}.discountPercentage`}
                          type="number"
                          min={0}
                          max={100}
                          {...register(`pricingTiers.${index}.discountPercentage` as const)}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`pricingTiers.${index}.price`} className="text-xs text-slate-600">Precio final</label>
                        <div className="flex h-8 items-center rounded-md bg-green-50 px-2 text-xs font-medium text-green-700">
                          {tierCalc.finalPrice ? `$${tierCalc.finalPrice}` : '—'}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          type="button"
                          onClick={() => removeTier(index)}
                          className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                          aria-label={`Eliminar nivel de precio ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="py-2 text-center text-xs text-purple-600">
                Sin niveles configurados. Haz clic en "Agregar Nivel" para comenzar.
              </p>
            )}
          </div>

          {/* Stock inicial */}
          <fieldset className="space-y-3 rounded-lg bg-slate-50 p-4">
            <legend className="sr-only">Configuración de Stock</legend>
            <h4 className="text-sm font-medium text-slate-900" aria-hidden="true">Configuración de Stock</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label htmlFor='minStock' className="text-sm font-medium text-slate-700">
                  Stock Mínimo <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                </label>
                <Input
                  id='minStock'
                  type="number"
                  min={0}
                  {...register('minStock')}
                  placeholder="0"
                  aria-invalid={!!errors.minStock}
                  aria-describedby={errors.minStock ? "minStock-error" : undefined}
                />
                <FormError id="minStock-error" message={errors.minStock?.message} />
                <p className="text-xs text-slate-500">Alerta de stock bajo</p>
              </div>
              <div className="space-y-2">
                <label htmlFor='maxStock' className="text-sm font-medium text-slate-700">
                  Stock Máximo <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                </label>
                <Input
                  id='maxStock'
                  type="number"
                  min={0}
                  {...register('maxStock')}
                  placeholder="0"
                  aria-invalid={!!errors.maxStock}
                  aria-describedby={errors.maxStock ? "maxStock-error" : undefined}
                />
                <FormError id="maxStock-error" message={errors.maxStock?.message} />
                <p className="text-xs text-slate-500">Capacidad máxima</p>
              </div>
              <div className="space-y-2">
                <label htmlFor='initialStock' className="text-sm font-medium text-slate-700">
                  Stock Inicial <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (campo obligatorio)</span>
                </label>
                <Input
                  id='initialStock'
                  type="number"
                  min={0}
                  {...register('initialStock')}
                  placeholder="0"
                  aria-invalid={!!errors.initialStock}
                  aria-describedby={errors.initialStock ? "initialStock-error" : undefined}
                />
                <FormError id="initialStock-error" message={errors.initialStock?.message} />
                <p className="text-xs text-slate-500">Cantidad de entrada</p>
              </div>
            </div>
          </fieldset>

          {/* Acciones */}
          <div className="flex gap-3 border-t border-slate-200 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              Agregar Producto
            </Button>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} className="flex-1">
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
